// ABOUTME: Keeps screening-questionnaire requests and persisted filters scoped to the active job.
// ABOUTME: Provides pure transformations used by list state and advanced-search controls.

import React from "react";

interface IQuestionnaireSearchRequestParams {
    companyId: number;
    jobId?: string;
    listApiAddress: string;
}

export const getQuestionnaireSearchRequest = (
    params: IQuestionnaireSearchRequestParams
) => {
    const queryString = params.listApiAddress.split("?")[1] || "";
    const isJobRequest =
        new URLSearchParams(queryString).get("isJobRequest") === "true";
    const requestParams: Compleo.IObject = {
        companyId: params.companyId
    };
    if (params.jobId) {
        requestParams.jobId = params.jobId;
    }

    return {
        endpoint: `/questionnaire/getquestionnairessearchelasticdata${
            isJobRequest ? "?isJobRequest=true" : ""
        }`,
        params: requestParams,
        isJobRequest
    };
};

// ABOUTME: List modules whose questionnaire fields belong to a single active job.
export const JOB_SCOPED_LIST_MODULES = [
    "JOBAPPLICANTLISTFILTERKANBAN",
    "JOBAPPLICANTLISTJOBVIEW"
];

// ABOUTME: true when a module may discover questionnaire fields in its current scope.
// ABOUTME: Job-scoped modules must wait for the active job; without it the backend
// ABOUTME: falls back to company-wide discovery and leaks other jobs' questions.
export const canRequestQuestionnaireFields = (
    moduleName: string,
    jobId?: string
): boolean =>
    !JOB_SCOPED_LIST_MODULES.includes(moduleName) || Boolean(jobId);

// ABOUTME: Returns the discovered fields only when they belong to the active scope.
// ABOUTME: Withholds fields whose cached query is not scoped to the active job:
// ABOUTME: queryJobId is the jobId on the query key behind the exposed response.
export const getRequestableQuestionnaireFields = (
    fieldsDef: Compleo.IObject[],
    moduleName: string,
    jobId?: string,
    queryJobId?: string
): Compleo.IObject[] =>
    canRequestQuestionnaireFields(moduleName, jobId) && queryJobId === jobId
        ? fieldsDef
        : [];

interface IScopeQuestionnaireFiltersParams {
    filters: Compleo.IObject;
    localInfo: Compleo.IObject;
    jobId?: string;
    otherGenericParams?: Compleo.IObject;
}

const normalizedJobId = (jobId: unknown) =>
    String(jobId || "")
        .replace(/^JOB:/i, "")
        .toUpperCase();

export const getAvailableQuestionnaireFields = (
    fields: Compleo.IObject[],
    selectedDefinitions: Compleo.IObject[]
) =>
    fields.filter(
        (field) =>
            !selectedDefinitions.some((definition) => {
                const searchDefinition = definition.otherSearchDef;
                return (
                    searchDefinition?.type === "questionnaire" &&
                    normalizedJobId(searchDefinition.jobId) ===
                        normalizedJobId(field.jobId) &&
                    searchDefinition.QuestionnaireId ===
                        field.QuestionnaireId &&
                    searchDefinition.QuestionId === field.QuestionId
                );
            })
    );

export const scopeQuestionnaireFiltersToJob = (
    params: IScopeQuestionnaireFiltersParams
) => {
    const { filters, localInfo, jobId, otherGenericParams } = params;
    if (!jobId) {
        return { filters, localInfo };
    }

    const valuesDefinition = filters?.advancedSearch?.valuesDefinition || [];
    const staleDefinitionIds = new Set(
        valuesDefinition
            .filter(
                (definition: Compleo.IObject) =>
                    definition.otherSearchDef?.type === "questionnaire" &&
                    normalizedJobId(definition.otherSearchDef?.jobId) !==
                        normalizedJobId(jobId)
            )
            .map((definition: Compleo.IObject) => definition.id)
    );

    const values = Object.fromEntries(
        Object.entries(filters?.advancedSearch?.values || {}).filter(
            ([fieldName]) =>
                ![...staleDefinitionIds].some((id) =>
                    fieldName.startsWith(`${id}_`)
                )
        )
    );

    return {
        filters: {
            ...filters,
            otherGenericParams: {
                ...(filters.otherGenericParams || {}),
                ...(otherGenericParams || {}),
                jobId
            },
            advancedSearch: {
                ...filters.advancedSearch,
                values,
                valuesDefinition: valuesDefinition.filter(
                    (definition: Compleo.IObject) =>
                        !staleDefinitionIds.has(definition.id)
                )
            }
        },
        localInfo: {
            ...localInfo,
            localMetadataReturn: (localInfo?.localMetadataReturn || []).filter(
                (metadata: Compleo.IObject) =>
                    !staleDefinitionIds.has(metadata.id)
            )
        }
    };
};

interface IUseQuestionnaireFilterScopeParams {
    filters: Compleo.IObject;
    localInfo: Compleo.IObject;
    jobId?: string;
    otherGenericParams?: Compleo.IObject;
    setFilters: React.Dispatch<React.SetStateAction<any>>;
    setLocalInfo: React.Dispatch<React.SetStateAction<any>>;
}

export const useQuestionnaireFilterScope = (
    params: IUseQuestionnaireFilterScopeParams
) => {
    const activeJobId = React.useRef(params.jobId);

    React.useEffect(() => {
        if (activeJobId.current === params.jobId) {
            return;
        }

        const scopedState = scopeQuestionnaireFiltersToJob(params);
        activeJobId.current = params.jobId;
        params.setFilters(scopedState.filters);
        params.setLocalInfo(scopedState.localInfo);
    }, [
        params.jobId,
        params.otherGenericParams,
        params.filters,
        params.localInfo,
        params.setFilters,
        params.setLocalInfo
    ]);
};
